import { InfoOutlined } from "@mui/icons-material";
import { Alert, AlertProps, styled, Typography } from "@mui/material";
import { ReactNode } from "react";

const StyledIcon = styled(InfoOutlined, {
  shouldForwardProp: (prop) => prop !== "alertVariant" && prop !== "severity",
})<{ alertVariant?: "default" | "usage"; severity: "info" | "warning" }>(({ severity, theme }) => ({
  color: severity === "warning" ? theme.palette.warning.main : theme.palette.info.main,
}));

const BoldedTypography = styled(Typography)({
  fontWeight: 600,
});

const StyledAlert = styled(Alert, {
  shouldForwardProp: (prop) => prop !== "severity",
})<{ severity: "info" | "warning" }>(({ severity, theme }) => ({
  ...(severity === "warning" && {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.chatTheme.statusAlert.warning.dark
        : theme.chatTheme.statusAlert.warning.light,
    color: theme.palette.text.primary,
    "& .MuiAlert-icon": {
      color: theme.palette.warning.main,
    },
    border: theme.palette.mode === "dark" ? `${theme.palette.warning.dark}` : "none",
  }),
  ...(severity === "info" && {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.chatTheme.statusAlert.info.dark
        : theme.chatTheme.statusAlert.info.light,
    color: theme.palette.text.primary,
    "& .MuiAlert-icon": {
      color: theme.palette.info.main,
    },
    border: theme.palette.mode === "dark" ? `${theme.palette.info.dark}` : "none",
  }),
}));

interface StatusAlertProps extends Omit<AlertProps, "icon"> {
  title?: string;
  children: ReactNode;
  severity?: "info" | "warning";
}

export const StatusAlert = ({
  title,
  children,
  severity = "info",
  sx,
  ...props
}: StatusAlertProps) => {
  return (
    <StyledAlert
      severity={severity}
      icon={<StyledIcon severity={severity} />}
      sx={{ mt: 2, ...sx }}
      {...props}
    >
      <Typography component="div" display="inline">
        {title && <BoldedTypography display="inline">{title}: </BoldedTypography>}
        {children}
      </Typography>
    </StyledAlert>
  );
};
